import { useState } from "react";
import { colors } from "@styles/colors";
import { useAddressStore } from "@utils/store/utils/useAddressStore";
import api, { handleRequest } from "@services/axios/apiManager";
import { AxiosResponse, AxiosError } from "axios";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { handleAPIAddressComponents } from "./API/handleAddressAPI";
import { isLocalAddressDevBypassEnabled, parseTaiwanAddressForDev } from "./func/devAddressFallback";
import { getAddressSelectionCoordinates, normalizeAddressSelectionData } from "./func/addressSelectionTool";
import { View } from "tamagui";
import RadiusBoxLabel from "@components/layout/RadiusBoxLabel";
import { RiMapPinLine, RiSearchLine } from "react-icons/ri";

interface AddressItem {
  route?: string;
  street_number?: string;
  administrative_area_level_1: string;
  administrative_area_level_2: string;
  place_id?: string;
}

interface AddressSearchResultProps {
  searchList?: AddressItem[];
  searchWord?: string;
  hasError?: boolean;
  autocompleteAPIResultType?: string;
}

const AddressSearchResult = ({ searchList = [], searchWord = "", hasError = false }: AddressSearchResultProps) => {
  const { lineAccessToken } = useLineAuth();
  const { setAutoCompleteAddress, setAddressViewMode, setMapPickerDisabled, autocompleteAPIData, mapPickerDisabled } = useAddressStore();
  const [isNormalizing, setIsNormalizing] = useState(false);
  const isLocalDevBypass = isLocalAddressDevBypassEnabled();

  if (!searchList?.length && !searchWord) return null;

  const handleAddressDetail = (searchItem: AddressItem) => {
    //-- 判斷 autocomplete API 為 Google 和目標地址也有 place_id，則跑 detail API 取得經緯度
    const finishSelection = (addressData = searchItem) => {
      const normalizedData = normalizeAddressSelectionData(addressData as Record<string, any>);
      const hasCoords = !!getAddressSelectionCoordinates(normalizedData);
      setAutoCompleteAddress(normalizedData);
      setAddressViewMode("addressForm");
      setMapPickerDisabled(hasCoords);
    };

    const { type, sessionToken } = autocompleteAPIData || {};
    const placeId = searchItem?.place_id;
    const shouldFetchDetail = type === "Google" && sessionToken && placeId;

    if (shouldFetchDetail) {
      handleRequest({
        asyncRequest: () =>
          api.get(`address-normalization-api/address/autocomplete/detail?place_id=${placeId}&sessionToken=${sessionToken}`, {
            baseURLType: "ADDRESS",
            headers: { Authorization: `Bearer ${lineAccessToken}` },
          }),
        successCallback: (res: AxiosResponse) => {
          const result = res.data?.data?.result;
          //-- 如果有 API 結果就用 API 的，否則用原始的 searchItem
          finishSelection(Array.isArray(result) ? result[0] : searchItem);
        },
        errorCallback: (error: any) => {
          console.error(error);
          finishSelection(); 
        },
      });
    } else {
      finishSelection();
    }
  };

  const openAddressForm = (addressData: Record<string, any> | null) => {
    const normalizedData = normalizeAddressSelectionData(addressData);
    setAutoCompleteAddress(normalizedData);
    setAddressViewMode("addressForm");
    const hasCoords = !!getAddressSelectionCoordinates(normalizedData);
    setMapPickerDisabled(hasCoords);
  };

  const handleManualSearchFallback = () => {
    const parsed = parseTaiwanAddressForDev(searchWord);
    if (!parsed) {
      console.warn("[AddressDebug][AddressSearchResult] local dev fallback parse failed", { searchWord });
      return;
    }
    console.warn("[AddressDebug][AddressSearchResult] local dev fallback parse success", { searchWord, parsed });
    openAddressForm(parsed);
  };

  return (
    <div>
      {searchList.length > 0 && (
        <ul>
          {searchList
            .filter((filterItem) => filterItem.route || filterItem.street_number)
            .map((searchItem, searchIndex) => (
              <View onPress={() => handleAddressDetail(searchItem)} key={`address-${searchIndex}`} my="$3">
                <RadiusBoxLabel
                  label={`${searchItem.route || ""}${searchItem.street_number || ""}`}
                  desc={`${searchItem.administrative_area_level_1}${searchItem.administrative_area_level_2}`}
                  icon={<RiMapPinLine color={colors.primary} />}
                />
              </View>
            ))}
        </ul>
      )}

      {/* 手動搜尋：用 normalization API 將輸入文字切分為地址欄位 */}
      <div className="address-item mt-3">
        <div className="icon-map">
          {isNormalizing ? (
            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <RiSearchLine color="#000" size={16} />
          )}
        </div>
        <div>
          <a
            href="#"
            onClick={async (e) => {
              e.preventDefault();
              if (isNormalizing) return;
              if (isLocalDevBypass && (!lineAccessToken || hasError)) {
                handleManualSearchFallback();
                return;
              }
              if (hasError) return;
              setIsNormalizing(true);
              const res = await handleAPIAddressComponents([searchWord], lineAccessToken || "");
              setIsNormalizing(false);
              const parsed = res.state === "success" && Array.isArray(res.data) ? res.data[0] : null;
              openAddressForm(parsed ?? null);
            }}>
            手動搜尋「 {searchWord} 」
          </a>
        </div>
      </div>
    </div>
  );
};

export default AddressSearchResult;
